import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';

import createSagaMiddleware from 'redux-saga';
import { all } from 'redux-saga/effects';
import { gpcRootReducer, IGPCGlobalState } from '@mitchell/redux.module.apd/dist/app/guidedPhotoCapture';
import {
	IPhotoCaptureGlobalState,
	photoCaptureRootReducer,
	photoCaptureRootSaga,
} from '@mitchell/redux.module.apd/dist/domain/photoCapture';
import {
	IQuestionnaireEvaluationGlobalState,
	questionnaireEvaluationRootReducer,
	questionnaireEvaluationRootSaga,
} from '@mitchell/redux.module.apd/dist/domain/questionnaireEvaluation';
import {
	attachmentRootReducer,
	attachmentRootSaga,
	IAttachmentGlobalState,
} from '@mitchell/redux.module.apd/dist/domain/attachment';
import {
	createResourceLookupState,
	IResourcesLookupGlobalState,
	resourceLookupRootReducer,
	resourceLookupRootSaga,
} from '@mitchell/redux.module.apd/dist/domain/resourcesLookup';
import { fileRootReducer, fileRootSaga, IFileGlobalState } from '@mitchell/redux.module.apd/dist/domain/file';
import { BaseAction } from '@mitchell/redux.module.apd/dist/domain/common/interfaces/BaseAction';
import { brandedNotificationRootSaga } from '@mitchell/redux.module.apd/dist/domain/brandedNotification';
import {
	vehicleInfoRootSaga,
	VehicleInfoGlobalState,
	vehicleResolved,
	VehicleSelectionMethod,
	VehicleInfoEntry,
	updateVehicleInfo,
	updateSelectedInfoEntries,
} from '@mitchell/redux.module.apd/dist/domain/vehicleInfo';
import {
	IVehicleConfigurationAttributesGlobalState,
	vehicleConfigurationAttributesRootReducer,
	vehicleConfigurationAttributesRootSaga,
} from '@mitchell/redux.module.apd/dist/domain/vehicleConfigurationAttributes';
import {
	IVehicleGlobalState,
	vehicleRootReducer,
	vehicleRootSaga,
} from '@mitchell/redux.module.apd/dist/domain/vehicle';
import { RESET_STORE } from '../constants';

type GPCShellGlobalState = IGPCGlobalState &
	IPhotoCaptureGlobalState &
	IQuestionnaireEvaluationGlobalState &
	IAttachmentGlobalState &
	IFileGlobalState &
	VehicleInfoGlobalState &
	IResourcesLookupGlobalState &
	IVehicleConfigurationAttributesGlobalState &
	IVehicleGlobalState;

const storeData: GPCShellGlobalState = require('./storeData.json');

export const getEnvironment = (): string => {
	const baseUrl = window.location.hostname.toLowerCase();

	// Check for non-Mitchell domains first
	if (!baseUrl.includes('.mymitchell.com')) {
		return 'DEV';
	}

	// Define environment mappings
	const environmentMappings = [
		{ urlPattern: 'ca-awsuat.mymitchell.com', environment: 'AWSUATCA' },
		{ urlPattern: 'ca-uat.mymitchell.com', environment: 'UATCA' },
		{ urlPattern: 'ca-awsprod.mymitchell.com', environment: 'AWSPRODCA' },
		{ urlPattern: 'ca.mymitchell.com', environment: 'PRODCA' },

		{ urlPattern: '-awsdev.mymitchell.com', environment: 'AWSDEV' },
		{ urlPattern: '-awsqa.mymitchell.com', environment: 'AWSQA' },
		{ urlPattern: '-awsuat.mymitchell.com', environment: 'AWSUAT' },

		{ urlPattern: '-dev.mymitchell.com', environment: 'DEV' },
		{ urlPattern: '-qa.mymitchell.com', environment: 'QA' },
		{ urlPattern: '-uat.mymitchell.com', environment: 'UAT' },
		{ urlPattern: '-awsprod.mymitchell.com', environment: 'AWSPROD' },
		{ urlPattern: '-dr.mymitchell.com', environment: 'DRPROD' },
	];

	// Find the first matching environment
	for (const { urlPattern, environment } of environmentMappings) {
		if (baseUrl.includes(urlPattern)) {
			return environment;
		}
	}
	// Default to PROD environment
	return 'PROD';
};

const sagaMiddleware = createSagaMiddleware();

const initialGlobalState: GPCShellGlobalState = {
	...storeData,
	domain: {
		questionnaireEvaluation: storeData.domain.questionnaireEvaluation,
		photoCapture: storeData.domain.photoCapture,
		attachment: storeData.domain.attachment,
		file: storeData.domain.file,
		vehicleInfo: {
			selectedInfoEntries: new Map<string, VehicleInfoEntry<string | number>>(),
			vehicleInfoData: {
				VehicleSelectionMethod: VehicleSelectionMethod.VinDecode,
				Vehicle: null,
			},
			hasVehicleResolved: false,
		},
		resourcesLookup: createResourceLookupState(),
		vehicleConfigurationAttributes: storeData.domain.vehicleConfigurationAttributes,
		vehicle: storeData.domain.vehicle,
	},
};

type Reducer<State, Action> = (prevState: State, action: Action) => State;

const appReducer: Reducer<any, BaseAction<any>> = combineReducers({
	app: combineReducers({
		guidedPhotoCapture: gpcRootReducer,
	}),
	domain: combineReducers({
		file: fileRootReducer,
		attachment: attachmentRootReducer,
		photoCapture: photoCaptureRootReducer,
		questionnaireEvaluation: questionnaireEvaluationRootReducer,
		vehicleInfo: combineReducers({
			vehicleInfoData: updateVehicleInfo,
			selectedInfoEntries: updateSelectedInfoEntries,
			hasVehicleResolved: vehicleResolved,
		}),
		vehicleConfigurationAttributes: vehicleConfigurationAttributesRootReducer,
		resourcesLookup: resourceLookupRootReducer,
		vehicle: vehicleRootReducer,
	}),
});

const rootReducer: Reducer<any, BaseAction<any>> = (state: GPCShellGlobalState, action: BaseAction<any>) => {
	if (action.type === RESET_STORE) {
		state = initialGlobalState;
	}
	const appState: GPCShellGlobalState = appReducer(state, action);
	appState.app = { ...state.app, ...appState.app };
	return appState;
};

window.mitchell = { environment: getEnvironment() };

function* rootSaga() {
	yield all([
		brandedNotificationRootSaga(),
		photoCaptureRootSaga(),
		questionnaireEvaluationRootSaga(),
		resourceLookupRootSaga(),
		vehicleConfigurationAttributesRootSaga(),
		vehicleRootSaga(),
		...attachmentRootSaga,
		...fileRootSaga,
		...vehicleInfoRootSaga,
	]);
}

export const Store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			thunk: false,
			immutableCheck: false,
			serializableCheck: false,
		}).prepend(sagaMiddleware),
	devTools: process.env.NODE_ENV !== 'production',
	preloadedState: initialGlobalState,
});

sagaMiddleware.run(rootSaga);
